import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { ToasterService } from '../../../shared/services/toaster/toaster.service';

@Component({
  selector: 'app-signup-password',
  templateUrl: './signup-password.component.html',
  styleUrls: ['./signup-password.component.scss']
})
export class SignupPasswordComponent implements OnInit {
  loading: boolean = false;
  email: string = '';
  password: string = '';
  confirmPassword: string = '';
  code: string = '';
  showPassword = false;
  passwordsMatch = false;
  
  passwordRequirements = {
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  };

  constructor(
    private router: Router,
    private authService: AuthService,
    private toaster: ToasterService
  ) { }

  ngOnInit(): void {
    // Retrieve email and code from session storage
    const storedEmail = sessionStorage.getItem('signupEmail');
    const storedCode = sessionStorage.getItem('signupCode');
    
    if (!storedEmail || !storedCode) {
      // If no email or code is found, redirect back to the appropriate step
      this.toaster.warning('Please complete the previous steps first', 4000);
      if (!storedEmail) {
        this.router.navigate(['/auth/signup/email']);
      } else {
        this.router.navigate(['/auth/signup/code']);
      }
      return;
    }
    
    this.email = storedEmail;
    this.code = storedCode;
  }

  /**
   * Completes the signup process by creating the account with the password
   */
  public async completeSignup(): Promise<void> {
    try {
      this.loading = true;

      // Guard against missing password
      if (!this.password) {
        this.toaster.warning('Please enter a password', 4000);
        this.loading = false;
        return;
      }

      // Check if all password requirements are met
      if (!this.allRequirementsMet()) {
        this.toaster.warning('Password does not meet all requirements.', 4000);
        this.loading = false;
        return;
      }

      // Check if passwords match
      if (this.password !== this.confirmPassword) {
        this.toaster.warning('Passwords do not match', 4000);
        this.loading = false;
        return;
      }

      const isSuccess = await this.authService.completeSignup(
        this.email,
        this.password,
        this.code
      ); // ← boolean result

      if (isSuccess) {
        sessionStorage.removeItem('signupEmail');
        sessionStorage.removeItem('signupCode');
        this.toaster.success('Account created successfully! Please log in.', 4000);
        this.router.navigate(['/auth/login']);
      } else {
        this.toaster.warning('Failed to create account. Please try again.', 4000);
      }
      
    } catch (err: any) {
      const msg =
        err?.error?.message       // backend message from NestJS
        ?? err?.message           // fallback: Angular generic
        ?? 'Failed to complete signup';

      console.error('Signup completion error:', err);
      this.toaster.warning(msg, 4000);

    } finally {
      this.loading = false;
    }
  }

  /**
   * Go back to the code verification step
   */
  public validatePassword(): void {
    this.passwordRequirements.length = this.password.length >= 8;
    this.passwordRequirements.uppercase = /[A-Z]/.test(this.password);
    this.passwordRequirements.lowercase = /[a-z]/.test(this.password);
    this.passwordRequirements.number = /[0-9]/.test(this.password);
    this.passwordRequirements.special = /[^A-Za-z0-9]/.test(this.password);
    this.checkPasswordsMatch();
  }

  public allRequirementsMet(): boolean {
    return Object.values(this.passwordRequirements).every(req => req);
  }

  public toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  public checkPasswordsMatch(): void {
    this.passwordsMatch = this.password !== '' && this.password === this.confirmPassword;
  }


  /**
   * Go back to the code verification step
   */
  public goBack(): void {
    this.router.navigate(['/auth/signup/code']);
  }
}
